import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, MapPin, Phone } from 'lucide-react';
import { serviceAreas, siteConfig } from '../data/mockData';
import LeafletMap from '../components/LeafletMap';

export default function ZonePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 to-neutral-50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-neutral-900 mb-6">
              Zone d'intervention – Infirmière à domicile
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed">
              Je me déplace dans les communes de Fleurus, Sambreville et Farciennes pour vous apporter 
              des soins infirmiers professionnels à domicile.
            </p>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-neutral-200 shadow-lg overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-neutral-50">
              <CardTitle className="text-2xl text-neutral-900">
                Carte de la zone de service
              </CardTitle>
              <p className="text-neutral-600 mt-2">
                Visualisez les communes où j'interviens pour les soins infirmiers à domicile
              </p>
            </CardHeader>
            
            <CardContent className="p-0">
              {/* Leaflet Map */}
              <LeafletMap />
              
              {/* Instructions */}
              <div className="bg-amber-50 border-t border-amber-200 p-4">
                <p className="text-sm text-amber-900">
                  <strong>Carte interactive OpenStreetMap</strong> - Les marqueurs indiquent nos zones d'intervention : 
                  Fleurus, Sambreville et Farciennes. Le cercle représente un rayon de 10 km.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Service Areas Details */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-neutral-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">
              Communes desservies
            </h2>
            <p className="text-lg text-neutral-600">
              Informations détaillées sur chaque zone d'intervention
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {serviceAreas.map((area) => (
              <Card key={area.id} className="border-neutral-200 bg-white hover:shadow-xl transition-all duration-300">
                <CardHeader className="bg-gradient-to-br from-amber-50 to-white">
                  <div className="bg-amber-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                    <MapPin className="h-6 w-6 text-amber-700" />
                  </div>
                  <CardTitle className="text-2xl text-neutral-900">
                    {area.name}
                  </CardTitle>
                  <p className="text-amber-700 font-medium">
                    Code postal : {area.postalCode}
                  </p>
                </CardHeader>
                
                <CardContent className="pt-6">
                  <p className="text-neutral-600 leading-relaxed mb-6">
                    {area.description}
                  </p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-neutral-600">
                      <span className="font-medium mr-2">Latitude :</span>
                      <span>{area.coordinates.lat.toFixed(4)}°N</span>
                    </div>
                    <div className="flex items-center text-neutral-600">
                      <span className="font-medium mr-2">Longitude :</span>
                      <span>{area.coordinates.lng.toFixed(4)}°E</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Descriptions */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Fleurus */}
          <div>
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Infirmière à domicile à Fleurus
            </h2>
            <p className="text-neutral-700 leading-relaxed mb-4">
              La commune de Fleurus bénéficie d'un service complet de soins infirmiers à domicile. 
              Située au cœur de la région, Fleurus et ses différents quartiers sont couverts par mes 
              interventions quotidiennes. Que vous résidiez au centre-ville ou dans les quartiers 
              périphériques, je me déplace rapidement pour assurer vos soins selon la prescription 
              de votre médecin traitant.
            </p>
            <p className="text-neutral-700 leading-relaxed">
              Les patients de Fleurus peuvent compter sur une disponibilité optimale et un service 
              de proximité. La connaissance du territoire local me permet d'organiser mes tournées 
              efficacement et de garantir une ponctualité maximale pour vos rendez-vous de soins.
            </p>
          </div>

          {/* Sambreville */}
          <div>
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Infirmière à domicile à Sambreville
            </h2>
            <p className="text-neutral-700 leading-relaxed mb-4">
              Sambreville et l'ensemble de ses sections (Auvelais, Tamines, Falisolle, Arsimont, Moignelée) 
              font partie intégrante de ma zone d'intervention. Les habitants de Sambreville bénéficient 
              d'un accès privilégié aux soins infirmiers à domicile, avec une attention particulière 
              portée à la coordination avec les médecins généralistes locaux et les structures de santé 
              de la région.
            </p>
            <p className="text-neutral-700 leading-relaxed">
              Service conventionné INAMI, possibilité de tiers payant selon votre situation, et 
              adaptation des horaires à vos contraintes personnelles pour un confort maximal dans 
              la réception de vos soins.
            </p>
          </div>

          {/* Farciennes */}
          <div>
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Infirmière à domicile à Farciennes
            </h2>
            <p className="text-neutral-700 leading-relaxed mb-4">
              Farciennes complète ma zone d'intervention principale. Les résidents de cette commune 
              peuvent solliciter mes services pour l'ensemble des soins infirmiers techniques prescrits 
              par leur médecin. L'accent est mis sur l'accompagnement des personnes âgées et des 
              patients nécessitant un suivi régulier à domicile.
            </p>
            <p className="text-neutral-700 leading-relaxed">
              La proximité géographique avec Fleurus et Sambreville permet une grande flexibilité 
              dans l'organisation des passages et garantit une réactivité optimale en cas de besoin 
              urgent de soins. Planning adapté à vos besoins et coordination avec votre famille pour 
              un suivi optimal.
            </p>
          </div>
        </div>
      </section>

      {/* Other Areas */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-amber-50 to-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-amber-200 shadow-lg">
            <CardContent className="p-8 text-center">
              <MapPin className="h-12 w-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-neutral-900 mb-4">
                Vous habitez une commune limitrophe ?
              </h3>
              <p className="text-neutral-700 leading-relaxed mb-6">
                Si vous résidez dans une commune proche de Fleurus, Sambreville ou Farciennes, 
                n'hésitez pas à me contacter. Selon ma disponibilité et la distance, je peux 
                envisager d'étendre mes interventions à votre secteur.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">
                  <Phone className="h-5 w-5 mr-2" />
                  Me contacter
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl p-8 lg:p-12 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Besoin de soins infirmiers dans votre commune ?
            </h2>
            <p className="text-xl text-amber-50 mb-8">
              Contactez-moi pour organiser votre première visite de soins à domicile
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${siteConfig.phone}`}>
                <Button size="lg" className="bg-white text-amber-700 hover:bg-amber-50 w-full sm:w-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Appeler maintenant
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-amber-600 w-full sm:w-auto">
                  Formulaire de contact
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
